import { UnnamedOverlay } from '@fullstory/proto/fs/services/dx-objects';
import { useQueryParam } from '@fullstory/react-hooks';
import {
  AppDispatch,
  defaultOverlayState,
  fullstorySelectors,
  OverlayRequestActions,
  unnamedOverlaySelectors,
} from '@fullstory/redux';
import { OVERLAY_QUERY_PARAM } from '@fullstory/routes';
import { hashObject } from '@fullstory/util/hash';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

/**
 * This hook takes the hash of a dashboard or virtual dashboard (or any view that uses
 * unnamed overlays) and determines if a new unnamed overlay needs to be created as
 * filters are changed. If the overlay values change, this hook contains the logic that
 * will create the new overlay and navigate to a new path that contains the new unnamed
 * overlay id as a `filter` query param.
 *
 * @returns filterCount is the number of filters applied in the overlay
 * @returns overlay is the unnamed overlay
 * @returns loading is the loading state of the request to fetch the overlay
 * @returns setOverlay is a function that will determine if a new overlay needs to be created
 * and create a new overlay + update the URL to include the new overlay hash if an overlay
 * is created.
 */
export const useUnnamedOverlay = (hash: string | undefined) => {
  const { overlay, loading } = useGetUnnamedOverlay(hash);
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const filterCount = useMemo(() => (overlay ? getFilterCount(overlay) : 0), [overlay]);
  const [_, setFilterQueryParam] = useQueryParam(OVERLAY_QUERY_PARAM, true);

  const setOverlay = useCallback(
    (overlay: UnnamedOverlay) => {
      const overlayWithoutId = { ...overlay, id: '' };
      const isDefaultUnnamed = isEqual(defaultOverlayState, overlayWithoutId);
      if (isDefaultUnnamed) {
        setFilterQueryParam(undefined);
      } else {
        const updatedHash = `${hashObject(overlayWithoutId)}`;
        dispatch(
          OverlayRequestActions.createUnnamedOverlay({
            orgId,
            hash: updatedHash,
            overlay: {
              ...overlay,
              id: updatedHash,
            },
          }),
        );
        setFilterQueryParam(updatedHash);
      }
    },
    [dispatch, orgId, setFilterQueryParam],
  );

  return {
    filterCount,
    overlay,
    loading,
    setOverlay,
  };
};

const useGetUnnamedOverlay = (hash: string | undefined | null) => {
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const overlaySelector = useMemo(
    () => unnamedOverlaySelectors.makeGetUnnamedOverlay(hash ?? ''),
    [hash],
  );
  const overlay = useSelector(overlaySelector);
  const requestState = useSelector(unnamedOverlaySelectors.getUnnamedOverlay);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!overlay && hash && !requestState.error)
      dispatch(
        OverlayRequestActions.getUnnamedOverlay({
          orgId,
          hash,
        }),
      );
  }, [overlay, orgId, hash, dispatch, requestState.error]);

  return useMemo(() => ({ ...requestState, overlay }), [requestState, overlay]);
};

export type OverlayFilter = ReturnType<typeof useUnnamedOverlayFromURL>;

/**
 * This hook will look at the URL and if the URL contains a filter hash, it will fetch
 * the unnamed overlay associated with the hash. The returned data includes the hash,
 * the overlay, and the loading / error state of the request to fetch the overlay.
 *
 * @returns OverlayFilter is an object with the filter hash, the overlay,
 * and the loading / error state of the request to fetch the overlay if a hash is present.
 */
export const useUnnamedOverlayFromURL = () => {
  const location = useLocation();
  const hash = useMemo(
    () => new URLSearchParams(location.search).get(OVERLAY_QUERY_PARAM) ?? undefined,
    [location.search],
  );
  const overlay = useGetUnnamedOverlay(hash);
  return useMemo(() => ({ ...overlay, hash }), [overlay, hash]);
};

export const getFilterCount = (overlay: UnnamedOverlay): number => {
  let count = 0;
  if (overlay.deps) {
    count += overlay.deps.length;
  }

  if (overlay.segments) {
    count += overlay.segments.length;
  }

  if (overlay.timeRange) {
    count += 1;
  }

  return count;
};
